// src/pages/admin/adminUserList.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box, IconButton, Typography, useMediaQuery, useTheme, Drawer,
  CircularProgress, Alert, Snackbar, Button, TextField, InputAdornment,
  Card, Chip, Tabs, Tab, Menu, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, FormControl, InputLabel, FormHelperText,
} from '@mui/material';
import {
  Search, FilterList, Refresh, Email, Phone, CheckCircle, Cancel,
  Add, Visibility, VisibilityOff, Person, Lock, Edit, ChevronLeft, ChevronRight,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import { useListAdminUsersQuery, useUpdateAdminUserMutation } from '../../features/admin/adminUserListApi.ts';
import { useListRolesQuery } from '../../features/admin/roleMasterApi.ts';

interface AdminUser {
  id: number;
  full_name: string;
  user_name: string;
  email: string;
  contact_no: string;
  role: string;
  status: number;
  createddt: string;
  updateddt: string | null;
}

// Cycling colour palette
const ROLE_COLOUR_PALETTE = [
  { bgcolor: '#fce4ec', color: '#c2185b' },
  { bgcolor: '#e3f2fd', color: '#1565c0' },
  { bgcolor: '#e8f5e9', color: '#2e7d32' },
  { bgcolor: '#fff3e0', color: '#e65100' },
  { bgcolor: '#f3e5f5', color: '#6a1b9a' },
  { bgcolor: '#e0f7fa', color: '#006064' },
  { bgcolor: '#fff8e1', color: '#f57f17' },
  { bgcolor: '#fbe9e7', color: '#bf360c' },
];

// Normalise role strings for comparison
// "super_admin" / "Super Admin" / "SUPER ADMIN" → all become "super admin"
const norm = (s: string) => (s || '').trim().toLowerCase().replace(/_/g, ' ');

const AdminUserList = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen]         = useState(false);
  const [searchStr, setSearchStr]           = useState('');
  const [tabValue, setTabValue]             = useState(0);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [snackbar, setSnackbar]             = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const scrollContainerRef                  = useRef<HTMLDivElement>(null);
  const [cacheBuster, setCacheBuster]       = useState(Date.now());

  // Add dialog
  const [addUserDialogOpen, setAddUserDialogOpen]     = useState(false);
  const [formData, setFormData]                       = useState({ full_name: '', user_name: '', email: '', contact_no: '', password: '', confirm_password: '', role: '' });
  const [showPassword, setShowPassword]               = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [registrationError, setRegistrationError]     = useState('');

  // Edit dialog
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editFormData, setEditFormData]             = useState({ id: 0, full_name: '', user_name: '', email: '', contact_no: '', role: '', status: 1, password: '' });
  const [showEditPassword, setShowEditPassword]     = useState(false);
  const [editError, setEditError]                   = useState('');

  // ── APIs ──────────────────────────────────────────────────────────────────
  const { data: rolesData, isLoading: rolesLoading, error: rolesError, refetch: refetchRoles } =
    useListRolesQuery(undefined);
  const roleOptions: any[] = rolesData?.roles || [];

  useEffect(() => {
    if (roleOptions.length > 0 && !formData.role)
      setFormData(p => ({ ...p, role: roleOptions[0].role_name }));
  }, [roleOptions]);

  const { data, isLoading, error, isError } = useListAdminUsersQuery({ _ts: cacheBuster });
  const [updateAdminUser, { isLoading: isUpdating }] = useUpdateAdminUserMutation();
  const adminUsers: AdminUser[] = data?.data || [];

  // ── Count users per role (normalised) ─────────────────────────────────────
  const roleCount = (role_name: string) =>
    adminUsers.filter(u => norm(u.role) === norm(role_name)).length;

  // ── VISIBLE TABS — "All" always shown + only roles with count > 0 ─────────
  // Each entry: { label, role_name, originalIndex }
  // originalIndex is used to keep chip colours consistent regardless of which
  // roles are currently visible.
  const visibleTabs = [
    { label: 'All', role_name: '', originalIndex: -1 },
    ...roleOptions
      .map((r: any, idx: number) => ({
        label:         r.role_name,
        role_name:     r.role_name,
        originalIndex: idx,           // preserve colour even when tabs are hidden
      }))
      .filter(t => roleCount(t.role_name) > 0),  // ✅ only tabs with users
  ];

  // Guard: if current tabValue points beyond visible tabs, reset to 0
  useEffect(() => {
    if (tabValue >= visibleTabs.length) setTabValue(0);
  }, [visibleTabs.length]);

  // ── Filtered rows ─────────────────────────────────────────────────────────
  const filteredUsers = (() => {
    const selectedRole = visibleTabs[tabValue]?.role_name || '';
    let rows = adminUsers;

    if (selectedRole) {
      const n = norm(selectedRole);
      rows = rows.filter(u => norm(u.role) === n);
    }

    if (searchStr) {
      const q = searchStr.toLowerCase();
      rows = rows.filter(u =>
        u.full_name?.toLowerCase().includes(q) ||
        u.user_name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.contact_no?.includes(searchStr) ||
        u.role?.toLowerCase().includes(q)
      );
    }
    return rows;
  })();

  // ── Role chip colour (uses originalIndex to keep colours stable) ──────────
  const getRoleColour = (userRole: string) => {
    const idx = roleOptions.findIndex(r => norm(r.role_name) === norm(userRole));
    if (idx === -1) return { bgcolor: '#f5f5f5', color: '#757575' };
    return ROLE_COLOUR_PALETTE[idx % ROLE_COLOUR_PALETTE.length];
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleScrollLeft    = () => scrollContainerRef.current?.scrollBy({ left: -300, behavior: 'smooth' });
  const handleScrollRight   = () => scrollContainerRef.current?.scrollBy({ left:  300, behavior: 'smooth' });
  const handleDrawerToggle  = () => setMobileOpen(v => !v);
  const handleSearch        = (e: React.ChangeEvent<HTMLInputElement>) => setSearchStr(e.target.value);
  const handleFilterClick   = (e: React.MouseEvent<HTMLElement>) => setFilterAnchorEl(e.currentTarget);
  const handleFilterClose   = () => setFilterAnchorEl(null);
  const handleTabChange     = (_: React.SyntheticEvent, v: number) => setTabValue(v);
  const handleCloseSnackbar = () => setSnackbar(s => ({ ...s, open: false }));

  const handleRefresh = () => {
    setCacheBuster(Date.now());
    refetchRoles();
    setSnackbar({ open: true, message: 'Data refreshed successfully', severity: 'success' });
  };

  // Add user
  const handleAddUserClick = () => {
    setAddUserDialogOpen(true);
    setFormData({ full_name: '', user_name: '', email: '', contact_no: '', password: '', confirm_password: '', role: roleOptions[0]?.role_name || '' });
    setRegistrationError(''); setShowPassword(false); setShowConfirmPassword(false);
  };
  const handleCloseAddUserDialog = () => { if (!registrationLoading) setAddUserDialogOpen(false); };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
    setRegistrationError('');
  };
  const handleRoleChange = (e: any) => { setFormData(p => ({ ...p, role: e.target.value })); setRegistrationError(''); };

  // Edit user
  const handleEditClick = (user: AdminUser) => {
    setEditFormData({ id: user.id, full_name: user.full_name || '', user_name: user.user_name || '', email: user.email || '', contact_no: user.contact_no || '', role: user.role || '', status: user.status, password: '' });
    setEditError(''); setShowEditPassword(false); setEditUserDialogOpen(true);
  };
  const handleCloseEditUserDialog  = () => { if (!isUpdating) setEditUserDialogOpen(false); };
  const handleEditInputChange      = (e: React.ChangeEvent<HTMLInputElement>) => { setEditFormData(p => ({ ...p, [e.target.name]: e.target.value })); setEditError(''); };
  const handleEditRoleChange       = (e: any) => { setEditFormData(p => ({ ...p, role: e.target.value }));             setEditError(''); };
  const handleEditStatusChange     = (e: any) => { setEditFormData(p => ({ ...p, status: parseInt(e.target.value) })); setEditError(''); };

  // Validation
  const validateEmail    = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const validatePhone    = (v: string) => /^\d{10}$/.test(v);
  const validatePassword = (v: string) => /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(v);

  // Update user
  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault(); setEditError('');
    if (!editFormData.full_name.trim())      return setEditError('Full name is required');
    if (!editFormData.user_name.trim())      return setEditError('Username is required');
    if (!editFormData.email.trim())          return setEditError('Email is required');
    if (!validateEmail(editFormData.email))  return setEditError('Please enter a valid email address');
    if (!editFormData.contact_no.trim())     return setEditError('Contact number is required');
    if (!validatePhone(editFormData.contact_no)) return setEditError('Please enter a valid 10-digit contact number');
    if (editFormData.password?.trim() && !validatePassword(editFormData.password))
      return setEditError('Password must be ≥8 chars with uppercase, lowercase, number & special character');
    if (!editFormData.role) return setEditError('Please select a role');
    try {
      const payload: any = { id: editFormData.id, full_name: editFormData.full_name.trim(), user_name: editFormData.user_name.trim(), email: editFormData.email.trim(), contact_no: editFormData.contact_no.trim(), role: editFormData.role, status: editFormData.status };
      if (editFormData.password?.trim()) payload.password = editFormData.password;
      const result = await updateAdminUser(payload).unwrap();
      if (!result.error) {
        setEditUserDialogOpen(false);
        setCacheBuster(Date.now());
        setSnackbar({ open: true, message: result.message || 'Admin user updated successfully!', severity: 'success' });
      } else setEditError(result.message || 'Update failed. Please try again.');
    } catch (err: any) { setEditError(err.data?.message || 'Update failed. Please try again.'); }
  };

  // Register user
  const handleRegisterUser = async (e: React.FormEvent) => {
    e.preventDefault(); setRegistrationLoading(true); setRegistrationError('');
    const fail = (msg: string) => { setRegistrationError(msg); setRegistrationLoading(false); };
    if (!formData.full_name.trim())          return fail('Full name is required');
    if (!formData.user_name.trim())          return fail('Username is required');
    if (!formData.email.trim())              return fail('Email is required');
    if (!validateEmail(formData.email))      return fail('Please enter a valid email address');
    if (!formData.contact_no.trim())         return fail('Contact number is required');
    if (!validatePhone(formData.contact_no)) return fail('Please enter a valid 10-digit contact number');
    if (!formData.password.trim())           return fail('Password is required');
    if (!validatePassword(formData.password)) return fail('Password must be ≥8 chars with uppercase, lowercase, number & special character');
    if (!formData.confirm_password.trim())   return fail('Please confirm your password');
    if (formData.password !== formData.confirm_password) return fail('Passwords do not match');
    if (!formData.role)                      return fail('Please select a role');
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_ENDPOINT}/admin/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', authkey: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3' },
        body: JSON.stringify({ full_name: formData.full_name.trim(), username: formData.user_name.trim(), email: formData.email.trim(), contact_no: formData.contact_no.trim(), password: formData.password, confirm_password: formData.confirm_password, role: formData.role, status: 1 }),
      });
      const result = await res.json();
      if (res.ok && !result.error) {
        setSnackbar({ open: true, message: result.message || 'Admin user created successfully!', severity: 'success' });
        setAddUserDialogOpen(false);
        setCacheBuster(Date.now());
        setFormData({ full_name: '', user_name: '', email: '', contact_no: '', password: '', confirm_password: '', role: roleOptions[0]?.role_name || '' });
      } else setRegistrationError(result.message || 'Registration failed.');
    } catch (err: any) {
      setRegistrationError(err.name === 'TypeError' ? 'Network error: Unable to connect.' : `Registration failed: ${err.message}`);
    } finally { setRegistrationLoading(false); }
  };

  const formatDateTime = (d: string) =>
    !d ? '-' : new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  const getStatusColor = (s: number) =>
    s === 1 ? { bgcolor: '#e8f5e9', color: '#2e7d32' } : { bgcolor: '#ffebee', color: '#c62828' };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Sidebar desktop */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Sidebar mobile */}
        <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}>
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />

          <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>

              {/* Page header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>Admin Users</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh}
                    sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500 }}>
                    Refresh
                  </Button>
                  <Button variant="contained" startIcon={<Add />} onClick={handleAddUserClick}
                    sx={{ textTransform: 'none', backgroundColor: '#008060', borderRadius: '8px', fontWeight: 500 }}>
                    Add New Admin User
                  </Button>
                </Box>
              </Box>

              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>

                {/* ── DYNAMIC TABS (only count > 0 shown) ───────────────── */}
                <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                  {(rolesLoading || isLoading) ? (
                    // Skeleton while data loads
                    <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} sx={{ color: '#008060' }} />
                      <Typography variant="body2" color="text.secondary">Loading tabs…</Typography>
                    </Box>
                  ) : (
                    <Tabs
                      value={tabValue}
                      onChange={handleTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                      TabIndicatorProps={{ style: { backgroundColor: '#008060' } }}
                      sx={{
                        px: 2,
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '13px',
                          color: '#6d7175',
                          minHeight: 48,
                          '&.Mui-selected': { color: '#008060', fontWeight: 600 },
                        },
                      }}
                    >
                      {visibleTabs.map((tab, idx) => {
                        const count = tab.role_name === '' ? adminUsers.length : roleCount(tab.role_name);
                        const colour = tab.originalIndex === -1
                          ? { bgcolor: '#e8f5e9', color: '#2e7d32' }   // "All" badge — green
                          : ROLE_COLOUR_PALETTE[tab.originalIndex % ROLE_COLOUR_PALETTE.length];

                        return (
                          <Tab
                            key={idx}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                <span>{tab.label}</span>
                                {/* ✅ Coloured badge showing count */}
                                <Box sx={{
                                  px: 0.9, py: 0.1,
                                  borderRadius: '20px',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  lineHeight: 1.6,
                                  bgcolor: tabValue === idx ? colour.bgcolor : '#f0f0f0',
                                  color:   tabValue === idx ? colour.color   : '#888',
                                  transition: 'all 0.2s ease',
                                  minWidth: 20,
                                  textAlign: 'center',
                                }}>
                                  {count}
                                </Box>
                              </Box>
                            }
                          />
                        );
                      })}
                    </Tabs>
                  )}
                </Box>

                {/* Search + Filter */}
                <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search by name, username, email, phone, or role"
                    value={searchStr}
                    onChange={handleSearch}
                    fullWidth
                    sx={{ maxWidth: 500, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: '#6d7175' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button variant="outlined" startIcon={<FilterList />} onClick={handleFilterClick}
                    sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500 }}>
                    Filter
                  </Button>
                  {/* Filter menu also only shows visible tabs */}
                  <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={handleFilterClose}>
                    {visibleTabs.map((tab, idx) => {
                      const count = tab.role_name === '' ? adminUsers.length : roleCount(tab.role_name);
                      return (
                        <MenuItem key={idx} onClick={() => { setTabValue(idx); handleFilterClose(); }}
                          sx={{ fontWeight: tabValue === idx ? 600 : 400 }}>
                          {tab.label}
                          <Chip label={count} size="small"
                            sx={{ ml: 1, height: 20, fontSize: '11px', fontWeight: 600,
                              ...(tab.originalIndex === -1
                                ? { bgcolor: '#e8f5e9', color: '#2e7d32' }
                                : ROLE_COLOUR_PALETTE[tab.originalIndex % ROLE_COLOUR_PALETTE.length])
                            }} />
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </Box>

                {/* Loading */}
                {isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                  </Box>
                )}

                {/* Error */}
                {isError && !isLoading && (
                  <Box sx={{ p: 3, bgcolor: 'white' }}>
                    <Alert severity="error">Failed to load admin users. Please try again.</Alert>
                  </Box>
                )}

                {/* Empty */}
                {!isLoading && !isError && filteredUsers.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>No users found</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {searchStr ? `No results for "${searchStr}"` : 'No admin users available'}
                    </Typography>
                  </Box>
                )}

                {/* ── TABLE ────────────────────────────────────────────────── */}
                {!isLoading && filteredUsers.length > 0 && (
                  <>
                    {/* Scroll nav */}
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, borderBottom: '1px solid #e1e3e5', bgcolor: '#fafbfb' }}>
                      <IconButton size="small" onClick={handleScrollLeft}
                        sx={{ color: '#6d7175', border: '1px solid #e1e3e5', borderRadius: '4px', width: 28, height: 28, '&:hover': { bgcolor: '#e1e3e5' } }}>
                        <ChevronLeft fontSize="small" />
                      </IconButton>
                      <Box sx={{ flex: 1, height: '4px', mx: 1, bgcolor: '#e1e3e5', borderRadius: '2px', overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', width: '30%', bgcolor: '#6d7175', borderRadius: '2px', opacity: 0.4 }} />
                      </Box>
                      <IconButton size="small" onClick={handleScrollRight}
                        sx={{ color: '#6d7175', border: '1px solid #e1e3e5', borderRadius: '4px', width: 28, height: 28, '&:hover': { bgcolor: '#e1e3e5' } }}>
                        <ChevronRight fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Scrollable table */}
                    <Box ref={scrollContainerRef} sx={{
                      bgcolor: 'white', overflowX: 'auto',
                      '&::-webkit-scrollbar': { height: '8px' },
                      '&::-webkit-scrollbar-track': { background: '#f1f2f4', borderRadius: '4px' },
                      '&::-webkit-scrollbar-thumb': { background: '#c1c4c8', borderRadius: '4px', '&:hover': { background: '#a1a4a8' } },
                    }}>
                      {/* Header */}
                      <Box sx={{ display: 'grid', gridTemplateColumns: '40px 160px 140px 210px 150px 170px 110px 165px 60px', gap: 1, px: 2, py: 1.5, borderBottom: '1px solid #e1e3e5', bgcolor: '#fafbfb', minWidth: '1280px' }}>
                        {['No', 'Full Name', 'Username', 'Email', 'Contact', 'Role', 'Status', 'Created', 'Actions'].map(h => (
                          <Typography key={h} variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px' }}>{h}</Typography>
                        ))}
                      </Box>

                      {/* Rows */}
                      {filteredUsers.map((user: AdminUser, index: number) => (
                        <Box key={user.id} sx={{
                          display: 'grid', gridTemplateColumns: '40px 160px 140px 210px 150px 170px 110px 165px 60px',
                          gap: 1, px: 2, py: 2, alignItems: 'center',
                          borderBottom: index < filteredUsers.length - 1 ? '1px solid #e1e3e5' : 'none',
                          '&:hover': { bgcolor: '#f9fafb' }, minWidth: '1280px',
                        }}>
                          <Typography variant="body2" sx={{ color: '#6d7175' }}>{index + 1}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{user.full_name || 'N/A'}</Typography>
                          <Typography variant="body2" color="text.secondary">{user.user_name || 'N/A'}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
                            <Email fontSize="small" sx={{ color: '#6d7175', flexShrink: 0 }} />
                            <Typography variant="body2" color="text.secondary" title={user.email}
                              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {user.email}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Phone fontSize="small" sx={{ color: '#6d7175', flexShrink: 0 }} />
                            <Typography variant="body2" color="text.secondary">{user.contact_no || 'N/A'}</Typography>
                          </Box>
                          <Chip label={user.role || 'N/A'} size="small"
                            sx={{ ...getRoleColour(user.role), fontWeight: 600, fontSize: '12px' }} />
                          <Chip
                            icon={user.status === 1 ? <CheckCircle sx={{ fontSize: 16 }} /> : <Cancel sx={{ fontSize: 16 }} />}
                            label={user.status === 1 ? 'Active' : 'Inactive'} size="small"
                            sx={{ ...getStatusColor(user.status), fontWeight: 600, fontSize: '12px' }} />
                          <Typography variant="body2" color="text.secondary">{formatDateTime(user.createddt)}</Typography>
                          <IconButton size="small" onClick={() => handleEditClick(user)} sx={{ color: '#008060' }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Add Dialog ───────────────────────────────────────────────────── */}
      <Dialog open={addUserDialogOpen} onClose={handleCloseAddUserDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', maxWidth: '500px' } }}>
        <DialogTitle sx={{ borderBottom: '1px solid #e1e3e5', pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person sx={{ color: '#008060' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Add New Admin User</Typography>
        </DialogTitle>
        <form onSubmit={handleRegisterUser}>
          <DialogContent sx={{ pt: 3 }}>
            {registrationError && <Alert severity="error" sx={{ mb: 2 }}>{registrationError}</Alert>}
            <TextField label="Full Name" name="full_name" fullWidth value={formData.full_name} onChange={handleInputChange} sx={{ mb: 2 }} disabled={registrationLoading} required
              InputProps={{ startAdornment: <InputAdornment position="start"><Person color="action" /></InputAdornment> }} />
            <TextField label="Username" name="user_name" fullWidth value={formData.user_name} onChange={handleInputChange} sx={{ mb: 2 }} disabled={registrationLoading} required placeholder="Enter username" />
            <TextField label="Email Address" name="email" type="email" fullWidth value={formData.email} onChange={handleInputChange} sx={{ mb: 2 }} disabled={registrationLoading} required
              InputProps={{ startAdornment: <InputAdornment position="start"><Email color="action" /></InputAdornment> }} />
            <TextField label="Contact Number" name="contact_no" fullWidth value={formData.contact_no} onChange={handleInputChange} sx={{ mb: 2 }} disabled={registrationLoading} required
              InputProps={{ startAdornment: <InputAdornment position="start"><Phone color="action" /></InputAdornment> }}
              inputProps={{ maxLength: 10 }} />
            <FormControl fullWidth sx={{ mb: 2 }} error={rolesLoading || !!rolesError}>
              <InputLabel>Role</InputLabel>
              <Select value={formData.role} onChange={handleRoleChange} label="Role" disabled={registrationLoading || rolesLoading}>
                {rolesLoading ? <MenuItem disabled><CircularProgress size={20} /> Loading roles...</MenuItem>
                  : rolesError ? <MenuItem disabled>Error loading roles</MenuItem>
                  : roleOptions.length === 0 ? <MenuItem disabled>No roles available</MenuItem>
                  : roleOptions.map((r: any) => <MenuItem key={r.id} value={r.role_name}>{r.role_name}</MenuItem>)}
              </Select>
              {rolesError && <FormHelperText>Failed to load roles. Please refresh.</FormHelperText>}
            </FormControl>
            <TextField label="Password" name="password" type={showPassword ? 'text' : 'password'} fullWidth
              value={formData.password} onChange={handleInputChange} sx={{ mb: 2 }} disabled={registrationLoading} required
              InputProps={{
                startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>,
                endAdornment: <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" disabled={registrationLoading}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>,
              }}
              helperText="Min 8 chars with uppercase, lowercase, number & special character" />
            <TextField label="Confirm Password" name="confirm_password" type={showConfirmPassword ? 'text' : 'password'} fullWidth
              value={formData.confirm_password} onChange={handleInputChange} disabled={registrationLoading} required
              InputProps={{
                startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>,
                endAdornment: <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" disabled={registrationLoading}>
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>,
              }} />
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid #e1e3e5' }}>
            <Button onClick={handleCloseAddUserDialog} disabled={registrationLoading} sx={{ color: '#eeeeee', bgcolor: '#f44336' }}>Close</Button>
            <Button onClick={handleCloseAddUserDialog} disabled={registrationLoading} sx={{ color: '#eeeeee', bgcolor: '#F78E12' }}>Cancel</Button>
            <Button type="submit" variant="contained"
              disabled={registrationLoading || rolesLoading || roleOptions.length === 0}
              sx={{ backgroundColor: '#008060' }}>
              {registrationLoading ? <><CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />Creating...</> : 'Create Admin User'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Edit Dialog ──────────────────────────────────────────────────── */}
      <Dialog open={editUserDialogOpen} onClose={handleCloseEditUserDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', maxWidth: '500px' } }}>
        <DialogTitle sx={{ borderBottom: '1px solid #e1e3e5', pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Edit sx={{ color: '#008060' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Edit Admin User</Typography>
        </DialogTitle>
        <form onSubmit={handleUpdateUser}>
          <DialogContent sx={{ pt: 3 }}>
            {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
            <TextField label="Full Name" name="full_name" fullWidth value={editFormData.full_name} onChange={handleEditInputChange} sx={{ mb: 2 }} disabled={isUpdating} required
              InputProps={{ startAdornment: <InputAdornment position="start"><Person color="action" /></InputAdornment> }} />
            <TextField label="Username" name="user_name" fullWidth value={editFormData.user_name} onChange={handleEditInputChange} sx={{ mb: 2 }} disabled={isUpdating} required
              InputProps={{ startAdornment: <InputAdornment position="start"><Person color="action" /></InputAdornment> }} />
            <TextField label="Email Address" name="email" type="email" fullWidth value={editFormData.email} onChange={handleEditInputChange} sx={{ mb: 2 }} disabled={isUpdating} required
              InputProps={{ startAdornment: <InputAdornment position="start"><Email color="action" /></InputAdornment> }} />
            <TextField label="Contact Number" name="contact_no" fullWidth value={editFormData.contact_no} onChange={handleEditInputChange} sx={{ mb: 2 }} disabled={isUpdating} required
              InputProps={{ startAdornment: <InputAdornment position="start"><Phone color="action" /></InputAdornment> }}
              inputProps={{ maxLength: 10 }} />
            <FormControl fullWidth sx={{ mb: 2 }} error={rolesLoading || !!rolesError}>
              <InputLabel>Role</InputLabel>
              <Select value={editFormData.role} onChange={handleEditRoleChange} label="Role" disabled={isUpdating || rolesLoading}>
                {rolesLoading ? <MenuItem disabled><CircularProgress size={20} /> Loading roles...</MenuItem>
                  : rolesError ? <MenuItem disabled>Error loading roles</MenuItem>
                  : roleOptions.length === 0 ? <MenuItem disabled>No roles available</MenuItem>
                  : roleOptions.map((r: any) => <MenuItem key={r.id} value={r.role_name}>{r.role_name}</MenuItem>)}
              </Select>
              {rolesError && <FormHelperText>Failed to load roles. Please refresh.</FormHelperText>}
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select value={editFormData.status} onChange={handleEditStatusChange} label="Status" disabled={isUpdating}>
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </Select>
            </FormControl>
            <TextField label="New Password (Optional)" name="password" type={showEditPassword ? 'text' : 'password'} fullWidth
              value={editFormData.password} onChange={handleEditInputChange} disabled={isUpdating}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>,
                endAdornment: <InputAdornment position="end">
                  <IconButton onClick={() => setShowEditPassword(!showEditPassword)} edge="end" disabled={isUpdating}>
                    {showEditPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>,
              }}
              helperText="Leave blank to keep current password" />
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid #e1e3e5' }}>
            <Button onClick={handleCloseEditUserDialog} disabled={isUpdating} sx={{ color: '#eeeeee', bgcolor: '#f44336' }}>Close</Button>
            <Button onClick={handleCloseEditUserDialog} disabled={isUpdating} sx={{ color: '#eeeeee', bgcolor: '#F78E12' }}>Cancel</Button>
            <Button type="submit" variant="contained"
              disabled={isUpdating || rolesLoading || roleOptions.length === 0}
              sx={{ backgroundColor: '#008060' }}>
              {isUpdating ? <><CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />Updating...</> : 'Update Admin User'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Snackbar ─────────────────────────────────────────────────────── */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUserList;